import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PMDialog } from "../../../utils/PMDialog";
import { DocumentArrowDownIcon } from "@heroicons/react/24/outline";
import { excelReports, exportReportsPdf } from "../../../../api/service/report";

export const ReportsProjectExportDialog = ({
  open,
  onClose,
  onExport,
  project,
  controller,
  period,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [exportSettings, setExportSettings] = useState({
    format: "excel", // "excel" or "pdf"
    include_report: true,
    include_customer_report: true,
    include_note: true,
  });

  const handleExport = async () => {
    setIsLoading(true);
    try {
      // Build export payload - use controller filters and period
      const payload = {
        ...controller,
        format: exportSettings.format === "excel" ? "xlsx" : "pdf",
        date_from: period?.from,
        date_to: period?.to,
        date_field: controller.date_field || "agreement_date",
      };

      // For Excel, use the excel endpoint which returns a URL
      if (exportSettings.format === "excel") {
        const response = await excelReports(payload);
        
        // The excel endpoint returns { excel_url: "..." }
        if (response?.data?.excel_url) {
          // Open the Excel file URL in a new window
          window.open(response.data.excel_url, "_blank");
        } else {
          console.error("No excel_url in response:", response);
          alert(t("Kunne ikke hente Excel-fil. Prøv igjen."));
        }
      } else {
        // For PDF, use the export_pdf endpoint
        const pdfPayload = {
          ...payload,
          include_report: exportSettings.include_report,
          include_customer_report: exportSettings.include_customer_report,
          include_note: exportSettings.include_note,
        };
        
        const response = await exportReportsPdf(pdfPayload);
        
        // Check if response is actually a PDF (starts with %PDF)
        const responseData = response.data;
        if (responseData instanceof Blob) {
          // Already a blob, use it directly
          const url = window.URL.createObjectURL(responseData);
          const link = document.createElement("a");
          link.href = url;
          
          const fileName = `rapporter_${project?.id || "project"}_${period?.from || "all"}_${new Date().toISOString().split('T')[0]}.pdf`;
          link.download = fileName;
          
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } else if (typeof responseData === 'string' && responseData.startsWith('%PDF')) {
          // String that starts with PDF header, convert to blob
          const blob = new Blob([responseData], {
            type: "application/pdf"
          });
          
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          
          const fileName = `rapporter_${project?.id || "project"}_${period?.from || "all"}_${new Date().toISOString().split('T')[0]}.pdf`;
          link.download = fileName;
          
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } else {
          // Check if it's an error response (JSON)
          try {
            const text = typeof responseData === 'string' ? responseData : JSON.stringify(responseData);
            const jsonData = JSON.parse(text);
            if (jsonData.errors || jsonData.error) {
              const errorMessage = jsonData.errors?.join(', ') || jsonData.error || 'Ukjent feil';
              throw new Error(errorMessage);
            }
          } catch (e) {
            // Not JSON, might be HTML error page
            console.error("PDF response is not valid:", responseData);
            throw new Error("Mottok ugyldig respons fra server. Sjekk konsollen for detaljer.");
          }
          
          // Fallback: try to create blob anyway
          const blob = new Blob([responseData], {
            type: "application/pdf"
          });
          
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          
          const fileName = `rapporter_${project?.id || "project"}_${period?.from || "all"}_${new Date().toISOString().split('T')[0]}.pdf`;
          link.download = fileName;
          
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }
      }

      if (onExport) {
        onExport(exportSettings);
      }
      
      onClose();
    } catch (error) {
      console.error("Error exporting reports:", error);
      alert(t("Kunne ikke eksportere rapporter. Prøv igjen."));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={t("Eksporter rapporter")}
      size="md"
    >
      <div className="space-y-6">
        {/* Format Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t("Format")}
          </label>
          <div className="flex gap-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="format"
                value="excel"
                checked={exportSettings.format === "excel"}
                onChange={(e) =>
                  setExportSettings({ ...exportSettings, format: e.target.value })
                }
                className="mr-2"
              />
              {t("Excel")}
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="format"
                value="pdf"
                checked={exportSettings.format === "pdf"}
                onChange={(e) =>
                  setExportSettings({ ...exportSettings, format: e.target.value })
                }
                className="mr-2"
              />
              {t("PDF")}
            </label>
          </div>
        </div>

        {/* Include Options - Only show for PDF */}
        {exportSettings.format === "pdf" && (
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">
              {t("Inkluder")}
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={exportSettings.include_report}
                onChange={(e) =>
                  setExportSettings({ ...exportSettings, include_report: e.target.checked })
                }
                className="mr-2"
              />
              {t("Rapporter")}
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={exportSettings.include_customer_report}
                onChange={(e) =>
                  setExportSettings({
                    ...exportSettings,
                    include_customer_report: e.target.checked,
                  })
                }
                className="mr-2"
              />
              {t("Kundrapporter")}
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={exportSettings.include_note}
                onChange={(e) =>
                  setExportSettings({ ...exportSettings, include_note: e.target.checked })
                }
                className="mr-2"
              />
              {t("Notater")}
            </label>
          </div>
        )}

        {/* Info */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <p className="text-sm text-blue-800">
            {t("Eksporten vil inkludere alle rapporter basert på valgte filtre og periode.")}
          </p>
        </div>

        {/* Period Info */}
        {period?.from && period?.to && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">
              <strong>{t("Periode")}:</strong> {period.from} - {period.to}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {t("Avbryt")}
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={isLoading}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {t("Eksporterer...")}
              </>
            ) : (
              <>
                <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                {t("Eksporter")}
              </>
            )}
          </button>
        </div>
      </div>
    </PMDialog>
  );
};

